// Tab Context Menu Component
// Architecture: v4.0.0 Multiple Charts Implementation
// Right-click context menu for chart tabs with rename, duplicate, close actions

import { devError } from '../../../utils/devLog';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, VStack, HStack, Text, Input, Button, Divider, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, useToast } from '@chakra-ui/react';
import {
  EditIcon,
  CopyIcon,
  CloseIcon,
  CheckIcon,
  SmallCloseIcon,
  SettingsIcon
} from '@chakra-ui/icons';
import { useMultipleCharts } from './MultipleChartsContext';
import { ChartInstance } from '../../../types/MultipleCharts';
import TabSettingsModal, { TabSettings } from './TabSettingsModal';

/**
 * Context Menu Position
 */
export interface ContextMenuPosition {
  x: number;
  y: number;
}

/**
 * Tab Context Menu Props
 */
export interface TabContextMenuProps {
  chartId: string | null;
  position: ContextMenuPosition | null;
  onClose: () => void;
}

/**
 * Tab Rename Modal Props
 */
interface TabRenameModalProps {
  chartId: string;
  currentName: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (chartId: string, newName: string) => Promise<void>;
}

/**
 * Tab Rename Modal Component
 * Modal for renaming chart tabs
 */
const TabRenameModal: React.FC<TabRenameModalProps> = ({
  chartId,
  currentName,
  isOpen,
  onClose,
  onSave
}) => {
  const [newName, setNewName] = useState(currentName);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewName(currentName);
      setIsLoading(false);
    }
  }, [isOpen, currentName]);

  const handleSave = useCallback(async () => {
    if (!newName.trim()) {
      toast({
        title: 'Invalid Name',
        description: 'Chart name cannot be empty',
        status: 'warning',
        duration: 2000,
        isClosable: true
      });
      return;
    }

    if (newName.trim() === currentName.trim()) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await onSave(chartId, newName.trim());
      toast({
        title: 'Chart Renamed',
        description: `Chart renamed to "${newName.trim()}"`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
      onClose();
    } catch (error) {
      devError('Failed to rename chart:', error);
      toast({
        title: 'Rename Failed',
        description: 'Failed to rename chart',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  }, [chartId, newName, currentName, onSave, onClose, toast]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }, [handleSave, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Rename Chart</ModalHeader>
        <ModalBody>
          <Input
            ref={inputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter chart name"
            maxLength={50}
            isDisabled={isLoading}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} mr={3} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isLoading}
            loadingText="Saving..."
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * Tab Context Menu Component
 * Right-click context menu for chart tabs
 */
export const TabContextMenu: React.FC<TabContextMenuProps> = ({
  chartId,
  position,
  onClose
}) => {
  const {
    charts,
    loadedChartIds,
    duplicateChart,
    closeTab,
    updateChart
  } = useMultipleCharts();
  
  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Get chart instance
  const chart = chartId ? charts.get(chartId) : null;
  
  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (position) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('contextmenu', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
    };
  }, [position, onClose]);

  // Close menu on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (position) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [position, onClose]);

  /**
   * Handle rename chart
   */
  const handleRename = useCallback(() => {
    onClose();
    onRenameOpen();
  }, [onClose, onRenameOpen]);

  /**
   * Handle rename save
   */
  const handleRenameSave = useCallback(async (chartId: string, newName: string) => {
    if (!chart) return;

    // Update chart metadata
    const updatedChart: Partial<ChartInstance> = {
      metadata: {
        ...chart.metadata,
        name: newName,
        modified: new Date()
      }
    };

    updateChart(chartId, updatedChart);
  }, [chart, updateChart]);

  /**
   * Handle duplicate chart
   */
  const handleDuplicate = useCallback(async () => {
    if (!chartId) return;
    
    setIsLoading(true);
    onClose();
    
    try {
      const newChartId = await duplicateChart(chartId);
      toast({
        title: 'Chart Duplicated',
        description: `Chart duplicated successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      devError('Failed to duplicate chart:', error);
      toast({
        title: 'Duplicate Failed',
        description: 'Failed to duplicate chart',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  }, [chartId, duplicateChart, onClose, toast]);

  /**
   * Handle close chart
   */
  const handleClose = useCallback(() => {
    if (!chartId) return;
    
    onClose();
    closeTab(chartId);
    
    toast({
      title: 'Chart Closed',
      description: 'Chart tab closed',
      status: 'info',
      duration: 2000,
      isClosable: true
    });
  }, [chartId, closeTab, onClose, toast]);

  /**
   * Handle settings menu open
   */
  const handleSettings = useCallback(() => {
    onClose();
    onSettingsOpen();
  }, [onClose, onSettingsOpen]);

  /**
   * Get current tab settings
   */
  const getCurrentTabSettings = useCallback((): TabSettings => {
    // Get current settings from chart metadata or defaults
    return {
      enableAnimations: chart?.settings?.enableAnimations ?? false,
      colorPreset: chart?.settings?.colorPreset ?? 'default'
    };
  }, [chart]);

  /**
   * Handle settings save
   */
  const handleSettingsSave = useCallback(async (chartId: string, settings: TabSettings) => {
    if (!chart) return;

    // Update chart with new settings
    const updatedChart: Partial<ChartInstance> = {
      settings: {
        ...chart.settings,
        ...settings
      },
      metadata: {
        ...chart.metadata,
        modified: new Date()
      }
    };

    updateChart(chartId, updatedChart);
  }, [chart, updateChart]);

  if (!position || !chartId || !chart) {
    return null;
  }

  // Calculate menu position to keep it within viewport
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(position.x, window.innerWidth - 200),
    top: Math.min(position.y, window.innerHeight - 150),
    zIndex: 10000
  };

  return (
    <>
      {/* Context Menu */}
      <Box
        ref={menuRef}
        style={menuStyle}
        bg="white"
        border="1px solid"
        borderColor="gray.300"
        borderRadius="md"
        boxShadow="lg"
        py={2}
        minW="180px"
        fontSize="sm"
      >
        {/* Chart Info Header */}
        <Box px={3} py={2} borderBottom="1px solid" borderColor="gray.100">
          <Text fontWeight="semibold" fontSize="xs" color="gray.600" isTruncated>
            {chart.metadata.name}
          </Text>
        </Box>

        <VStack spacing={0} align="stretch">
          {/* Rename */}
          <HStack
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            onClick={handleRename}
          >
            <EditIcon boxSize={3} color="blue.500" />
            <Text>Rename</Text>
          </HStack>

          {/* Duplicate */}
          <HStack
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            onClick={handleDuplicate}
            opacity={isLoading ? 0.6 : 1}
            pointerEvents={isLoading ? 'none' : 'auto'}
          >
            <CopyIcon boxSize={3} color="green.500" />
            <Text>Duplicate</Text>
          </HStack>

          {/* Settings */}
          <HStack
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            onClick={handleSettings}
          >
            <SettingsIcon boxSize={3} color="purple.500" />
            <Text>Settings</Text>
          </HStack>

          <Divider />

          {/* Close (only if more than 1 tab) */}
          {loadedChartIds.length > 1 && (
            <HStack
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'red.50' }}
              onClick={handleClose}
            >
              <CloseIcon boxSize={3} color="red.500" />
              <Text color="red.500">Close Tab</Text>
            </HStack>
          )}
        </VStack>
      </Box>

      {/* Rename Modal */}
      {chart && (
        <TabRenameModal
          chartId={chartId}
          currentName={chart.metadata.name}
          isOpen={isRenameOpen}
          onClose={onRenameClose}
          onSave={handleRenameSave}
        />
      )}

      {/* Settings Modal */}
      {chart && (
        <TabSettingsModal
          chartId={chartId}
          currentSettings={getCurrentTabSettings()}
          isOpen={isSettingsOpen}
          onClose={onSettingsClose}
          onSave={handleSettingsSave}
        />
      )}
    </>
  );
};

export default TabContextMenu;